import { useEffect, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useNowTicker } from "@/hooks/useNowTicker";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { Label } from "@/components/ui/base/label";
import { useStateMachine } from "@/core/arch/state_machine";
import { JobBloc } from "@/application/tools/job_bloc";
import { productUpdateBroadcastJobRepository } from "@/application/tools/job_repository";
import { ToolSheetShell } from "./ToolSheetShell";
import { JobStatusBadge } from "./JobStatusBadge";
import { formatDuration } from "./job_display";
import type { ToolSheetProps } from "../tools";

const TITLE_MAX = 120;
const BODY_MAX = 1000;

type Step = "compose" | "confirm";

/**
 * Composes and sends one authored product update to every merchant.
 *
 * The broadcast id is minted once, on mount, and carried through every kickoff
 * attempt made from this sheet instance — including a retry after a failed
 * send. The backend job is keyed on that id, so resending after a timeout or
 * error reaches only merchants the first attempt missed rather than notifying
 * the whole base twice.
 *
 * That id lives only in this component, so closing the sheet destroys the only
 * thing standing between a partial run and a duplicate send. Closing mid-flight
 * is therefore confirmed, and a terminal run offers "Run again" under the same
 * id rather than forcing a reopen that would mint a new one.
 */
export function ProductUpdateBroadcastSheet({ onClose }: ToolSheetProps) {
  const [broadcastId] = useState(() => crypto.randomUUID());
  const [step, setStep] = useState<Step>("compose");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");

  const [state, bloc] = useStateMachine(
    () =>
      new JobBloc(
        productUpdateBroadcastJobRepository,
        "product-update-broadcast",
      ),
  );

  useEffect(() => {
    bloc.resumeIfRunning();
  }, [bloc]);

  const { phase } = state;
  const job =
    phase.kind === "watching" || phase.kind === "cancelling" ? phase.job : null;

  const isRunning = job?.status === "running" || job?.status === "pending";
  const now = useNowTicker(isRunning);
  const elapsed = job
    ? formatDuration(job.startedAt, job.completedAt, now)
    : null;
  const isTerminal =
    job?.status === "complete" ||
    job?.status === "failed" ||
    job?.status === "cancelled";

  const canReview = title.trim().length > 0 && body.trim().length > 0;
  const canSend =
    step === "confirm" && (phase.kind === "idle" || phase.kind === "error");

  const inFlight =
    phase.kind === "kicking_off" ||
    phase.kind === "cancelling" ||
    (phase.kind === "watching" && isRunning);

  const handleSend = () => {
    void bloc.kickoff({
      broadcastId,
      title: title.trim(),
      body: body.trim(),
      linkUrl: link.trim() ? link.trim() : null,
    });
  };

  /**
   * Returns the sheet to its confirm step under the same broadcast id, so a
   * resend after a partial or failed run dedupes every merchant the first run
   * already reached instead of notifying them twice.
   */
  const handleRunAgain = () => {
    bloc.reset();
    setStep("confirm");
  };

  /**
   * Closing while a broadcast is in flight discards the broadcast id with it —
   * a later resend would then carry a fresh id and duplicate everyone already
   * notified. `ToolSheetShell` routes escape, the backdrop and the X through
   * this one callback, so guarding here guards all three.
   */
  const requestClose = () => {
    if (
      inFlight &&
      !window.confirm(
        "This broadcast is still sending. Closing loses the id that stops merchants being notified twice — resending later would reach everyone again. Close anyway?",
      )
    ) {
      return;
    }
    onClose();
  };

  return (
    <ToolSheetShell
      title="Product Update Broadcast"
      onClose={requestClose}
      footer={
        <div className="flex gap-2">
          {step === "compose" && (
            <Button
              className="flex-1"
              disabled={!canReview}
              onClick={() => setStep("confirm")}
            >
              Review
            </Button>
          )}
          {step === "confirm" && canSend && (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setStep("compose")}
              >
                Back
              </Button>
              <Button className="flex-1" onClick={handleSend}>
                Send broadcast
              </Button>
            </>
          )}
          {phase.kind === "kicking_off" && (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Sending…
            </Button>
          )}
          {phase.kind === "watching" && isRunning && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => void bloc.cancel()}
            >
              Stop sending
            </Button>
          )}
          {phase.kind === "cancelling" && (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Stopping…
            </Button>
          )}
          {isTerminal && (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleRunAgain}
              >
                Run again
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Done
              </Button>
            </>
          )}
        </div>
      }
    >
      {step === "compose" && (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Sends one authored update to every merchant as an informational
            notification. Merchants who switched product updates off receive
            nothing.
          </p>

          <div>
            <Label htmlFor="broadcast-title">Title</Label>
            <Input
              id="broadcast-title"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Instant payouts are here"
            />
            <p className="text-subtle-foreground mt-1 text-xs">
              {title.length}/{TITLE_MAX}
            </p>
          </div>

          <div>
            <Label htmlFor="broadcast-body">Body</Label>
            <textarea
              id="broadcast-body"
              value={body}
              maxLength={BODY_MAX}
              onChange={(event) => setBody(event.target.value)}
              rows={6}
              placeholder="Payouts now settle same day for eligible merchants."
              className="border-border text-foreground w-full rounded-md border p-2 text-sm focus:border-blue-400 focus:outline-none"
            />
            <p className="text-subtle-foreground mt-1 text-xs">
              {body.length}/{BODY_MAX}
            </p>
          </div>

          <div>
            <Label htmlFor="broadcast-link">Link (optional)</Label>
            <Input
              id="broadcast-link"
              value={link}
              onChange={(event) => setLink(event.target.value)}
              placeholder="https://cpos.com/…"
            />
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div className="space-y-4">
          <div className="border-border bg-muted rounded-md border p-3">
            <p className="text-foreground text-sm font-semibold">{title}</p>
            <p className="text-muted-foreground mt-1 text-sm whitespace-pre-wrap">
              {body}
            </p>
            {link.trim() && (
              <p className="text-muted-foreground mt-2 text-xs break-all">
                {link.trim()}
              </p>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            This goes out once, as an informational notification, to{" "}
            <strong>every merchant</strong> who has not switched product updates
            off. Sending the same broadcast again will not notify anyone twice.
          </p>
        </div>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{phase.message}</p>
        </div>
      )}

      {job && (
        <div className="border-border mt-6 space-y-2 border-t pt-4">
          <div className="flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            {elapsed && (
              <span className="text-subtle-foreground text-xs">{elapsed}</span>
            )}
          </div>

          {job.status === "failed" && job.error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">
                Broadcast did not complete cleanly
              </p>
              <pre className="mt-2 overflow-x-auto text-xs text-red-600">
                {job.error}
              </pre>
            </div>
          )}

          <p className="text-subtle-foreground text-xs">Job ID: {job.id}</p>
        </div>
      )}
    </ToolSheetShell>
  );
}
